// Claude decision layer — client for the daily strategy build, one-tap confirm
// and post-racing review. Mirrors the developer brief's daily flow:
// build payload → call Claude → staged card → confirm → place → review.
import { API_BASE_URL } from '../config';
import { authHeader, readApiJson } from './auth';

export interface RaceRef {
  meetingName: string;
  raceNumber: number;
}

export interface BankInput {
  current?: number;
  running_pnl?: number;
  stop_loss?: number;
  // Per-bet risk ceiling as a 0–1 fraction of bank (UI sets this as a %).
  max_stake_fraction?: number;
  // v2.3 Ch5: flat stake as a 0–1 fraction of the live bank (the staking lever).
  flat_fraction?: number;
  // Level staking: 0–1 share of bank to deploy across the day (÷ bet count).
  daily_deploy_pct?: number;
  // v2.4 Change 9 — the commitment slider: 0–1 share of the LIVE balance that becomes
  // today's bank. Every stake is sized off (live balance × this).
  commitment_pct?: number;
  // Operator-selectable staking mode per run: 'flat' = fixed-unit staked plan,
  // 'flat_fraction' = % of live bank per bet (strategy default). ('kelly' stays
  // calibration-gated server-side.) flat_unit is the fixed $/bet for 'flat'.
  stake_mode?: 'flat' | 'flat_fraction' | 'daily_split' | 'kelly';
  flat_unit?: number;
}

export interface GateCheck {
  gate: string;
  status: 'pass' | 'fail' | 'na';
  note: string;
}

export interface StagedBet {
  track: string;
  race: number;
  runner: string;
  side: 'back' | 'lay';
  structure: 'single' | 'accumulator' | 'dutch';
  legs: Array<{ track: string; race: number; runner: string }>;
  price_at_decision: number | null;
  suggested_price: number | null;
  fair_price: number | null;
  edge_pct: number | null;
  fair_prob: number | null;
  offered_prob: number | null;
  stake: number;
  key_signal: string | null;
  decision_logic: string | null;
  gate_checks: GateCheck[];
  reasons: string[];
  risks: string[];
  confidence: 'low' | 'medium' | 'high' | null;
  confidence_rationale: string | null;
  // v2.4 §4.5 — WIN/PLACE tag for the bet slate, + place-engine fields.
  market_type?: 'WIN' | 'PLACE' | null;
  place_prob?: number | null; // model place probability (%), place bets only
  place_paid?: number | null; // paid places on the place market
  // v2.4 §4 — favourite-assessment state of the bet's race (1/2/3/4).
  race_state?: 1 | 2 | 3 | 4 | null;
  race_state_label?: string | null;
  // v2.4 Change 9 — race jump time (ISO), for the slate's chronological sort.
  start_time?: string | null;
  market_id: string | null;
  selection_id: number | null;
  status: 'selected' | 'selected_not_staked' | 'staged' | 'placed' | 'failed' | 'unplaceable';
  result: 'win' | 'lose' | 'void' | 'no_bet' | 'unknown' | null;
  would_result?: 'win' | 'lose' | 'unknown';
  starting_price: number | null;
  clv: number | null;
  bet_id: string | null;
  matched_price?: number | null;
  placed_stake?: number | null;
  dutch_profitable?: boolean;
  dutch_overround?: number;
  // Staking-Separation Spec: per-pick platform routing + the Button-2 staking outcome.
  platform?: 'BETFAIR' | 'TAB' | 'CASH' | null;
  staking_pool?: 'onbase' | 'value' | null;
  staking_note?: string | null;
  staking_level_return?: number | null;
  // v2.2 scoring-engine cross-check (independent model rating for this runner).
  engine_fair_price?: number | null;
  engine_rank?: number | null;
  engine_win_pct?: number | null;
  engine_confidence?: string | null;
  // Execution spec: where to rest the offer, when to work it, the buffer it cleared.
  value_limit_price?: number | null;
  execution_window?: 'early' | 'late' | 'default' | null;
  value_buffer_pct?: number | null;
  exposure_scaled?: boolean;
  // Set by /reprice when the live-market edge has gone (Kelly stake 0) — the pick is
  // kept visible but flagged so the user decides whether to still place it.
  no_value?: boolean;
  repriced_at?: string;
  // v2.3 Ch7/Ch8 — On-Base winner-finder + conviction/agreement.
  engine_source?: 'value' | 'on_base' | 'both' | 'fair_fav' | null;
  // Canonical engine label for display/parity (server engineLabelFor): the engine that
  // OWNS this pick — ON_BASE | VALUE | FALSE_FAV | FAIR_FAV | PLACE. 'both' reports ON_BASE.
  engine_label?: 'ON_BASE' | 'VALUE' | 'FALSE_FAV' | 'FAIR_FAV' | 'PLACE' | string | null;
  onbase_floor_fill?: boolean; // taken to meet the daily On-Base minimum (sub-bar standout)
  conviction?: number | null; // v2.5 On-Base conviction score (0–100%)
  intent_score?: number | null;
  intent_signals?: string[];
  conviction_gap?: number | null;
  agreement?: number | null;
  stake_multiplier?: number | null;
  // v2.3 Ch3 reliability (%) + Ch6 coarse late-money read (Δ prob-points).
  reliability?: number | null;
  late_money_pts?: number | null;
  // v2.32 — the exact bucket this pick belongs to (engine × state × criterion), computed server-side
  // from the bet's own fields so it can never drift from the Buckets/Ledger pages' own taxonomy.
  // e.g. 'ON_BASE|S2|GENUINE' / 'On-Base · S2 · Genuine standout' — there are several On-Base
  // buckets, and the generic engine name alone doesn't say which one this pick is.
  policy_id?: string | null;
  bucket_label?: string | null;
  // v2.32 — true if this pick's bucket is in the currently-approved weekend live-gating allow-list
  // (Place S1 / On-Base S2 Genuine / On-Base S1 Bootstrap / Value S4 at $20+ BSP), false if simple
  // gating is on but this bucket isn't allow-listed, null if simple gating is off entirely (no
  // allow-list in force). Lets a manual bettor see which picks are actually trusted right now.
  bucket_live_approved?: boolean | null;
}

// v2.2 deterministic scoring engine — per-day rating + data coverage.
export interface EngineSummary {
  version: string;
  coverage: { overall: number; by_factor: Record<string, number> };
  factor_names: Record<string, string>;
  factor_weights: Record<string, number>;
  calibration?: { beta: number; _meta?: { test_top1?: number; fitted_at_races?: number } } | null;
  races: Array<{
    track: string;
    race: number;
    field_size: number;
    coverage: number;
    // v2.4 §4 — favourite-assessment state per race.
    race_state?: 1 | 2 | 3 | 4 | null;
    race_state_label?: string | null;
    race_state_action?: string | null;
    top: Array<{ runner: string; rank: number; fair_price: number | null; win_pct: number; confidence: string }>;
  }>;
}

// Staking-Separation Spec — the Button-2 pass result.
export interface StakingSummary {
  staked_at: string;
  commitment_pct: number;
  onbase_share: number;
  kelly_fraction: number;
  live_bank: number | null;
  base_balance: number;
  day_spend: number;
  onbase_pool: number;
  value_pool: number;
  available: number;
  committed: number;
  scaled: boolean;
  day_pct: number | null;
  staked_count: number;
  selected_not_staked_count: number;
  deselected_count: number;
  value_floor_failed: number;
  empty_pool_policy: string;
}

export interface NoBetRace {
  track: string;
  race: number;
  reason: string;
  closest_runner?: string;
  what_would_change_it?: string;
}

export interface StrategyReview {
  review: string;
  bets: Array<{ track: string; race: number; runner: string; result: string; comment: string }>;
  metrics: Record<string, number>;
  computed_metrics?: Record<string, number>;
  lessons?: string[];
  reviewed_at?: string;
}

export interface ConsideredRace {
  meeting_name: string;
  race_number: number;
  field_size: number;
  betfair_market_id: string | null;
}

export interface StrategyRun {
  id: string;
  date: string;
  status: 'building' | 'staged' | 'stop_loss' | 'reviewed';
  day_brief: string;
  market_read: string;
  key_themes: string[];
  races: ConsideredRace[];
  bets: StagedBet[];
  no_bet_races: NoBetRace[];
  notes_feedback: string;
  bank: {
    current: number; running_pnl: number; stop_loss: number;
    max_stake_fraction?: number; source?: string;
    // v2.3 Ch5 staking discipline.
    opening?: number; flat_fraction?: number; stop_loss_pct?: number; profit_lock_pct?: number;
    stake_mode?: string; daily_deploy_pct?: number;
    // v2.4 Change 9 + §1/§2: commitment model, calibration-gated staking, no daily limits.
    live_balance?: number | null; commitment_pct?: number;
    requested_stake_mode?: string; calibration_passed?: boolean;
    staking_stage?: 'flat' | 'kelly'; kelly_fraction?: number;
    daily_limits_enabled?: boolean;
  } | null;
  stop_loss_hit: boolean;
  // Staking-Separation Spec: selection vs staking pass state + the Button-2 summary.
  staking_state?: 'unstaked' | 'staked' | null;
  staking_summary?: StakingSummary | null;
  // v2.4 §4 — day-level favourite-assessment tally.
  race_state_summary?: { 1: number; 2: number; 3: number; 4: number; unclassified?: number; routing?: boolean; devig?: string } | null;
  model: string | null;
  selection_mode?: 'model' | 'claude' | null;
  engine: EngineSummary | null;
  review: StrategyReview | null;
  created_at: string;
}

const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...authHeader() });

const requestAuth = new WeakMap<Response, string | undefined>();
const strategyFetch: typeof fetch = async (input, init) => {
  const authorization = new Headers(init?.headers).get('Authorization') || undefined;
  const response = await fetch(input, init);
  requestAuth.set(response, authorization);
  return response;
};
const handle = (res: Response) => readApiJson(res, requestAuth.get(res));

// The daily run always considers the whole card for the date — no per-race
// selection. It is idempotent per day: the server returns the existing frozen
// run unless `replace` is set (a deliberate re-run that supersedes the day's run).
export const buildStrategy = async (
  date: string,
  bank?: BankInput,
  includeNotes = true,
  replace = false,
  metroOnly = true,
  // Decision-Engine cut-over: run the day on the internal engine with ZERO Claude API
  // calls (selection/labels/reasons all from code). On by default so the daily run no
  // longer asks Claude; Claude is used only for on-demand discussion / reviews.
  engineOnly = true
): Promise<StrategyRun> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/build`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ date, bank, includeNotes, replace, metroOnly, engineOnly }),
  });
  return handle(res);
};

// Live Betfair account status — used to show the bank (= available balance) and the
// exchange minimum stake on the strategy page, read-only.
export interface BetfairStatus {
  configured: boolean;
  account: {
    balance: number;
    exposure: number;
    exposureLimit: number;
    discountRate: number;
    pointsBalance: number;
    currency: string;
  };
  minStake: number;
}

export const getBetfairStatus = async (): Promise<BetfairStatus> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/betfair/status`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface RepriceSummary {
  bank: number | null; // committed bank stakes are sized from (live balance × commitment)
  live_balance?: number | null;
  staged_repriced: number;
  orders_repriced: number;
  staged: Array<{ runner: string; track: string; race: number; from_price: number | null; to_price: number; from_stake: number; to_stake: number; edge_pct: number | null }>;
  orders: Array<{ runner: string; market: string; from: number; to: number; size: number }>;
}

// Re-price the run's offers against the current live market: refresh staged bets'
// price/edge/stake AND move any placed-but-unmatched orders to the live best price.
// v2.4 Change 9 — an optional commitment % (0–1) re-slices today's bank off the live
// balance before re-sizing; omit to keep the run's stored commitment.
export const repriceStrategyRun = async (
  runId: string,
  commitmentPct?: number
): Promise<{ run: StrategyRun; summary: RepriceSummary }> => {
  const body = commitmentPct != null ? { commitment_pct: commitmentPct } : {};
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${runId}/reprice`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res);
};

// Staking-Separation Spec — Button 2. Stake the post-opt-out pick list: split the day
// spend 65/35, Dutch the On-Base pool by price, quarter-Kelly the Value pool, route each
// pick to its platform's price. Recomputed from scratch each call. Nothing is placed.
export interface StakeInput {
  commitment_pct?: number;          // 0–1 share of live balance committed today
  onbase_share?: number;            // 0–1 On-Base share of the day spend (Value = 1 − this)
  kelly_fraction?: number;          // Value-pool Kelly fraction
  deselected?: number[];            // bet indexes opted out before staking
  routes?: Record<number, 'BETFAIR' | 'TAB' | 'CASH'>;  // per-pick platform
  prices?: Record<number, number>;  // per-pick price override (the price you'll get there)
}

export const stakeStrategyRun = async (
  runId: string,
  input: StakeInput
): Promise<{ run: StrategyRun; summary: StakingSummary }> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${runId}/stake`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input || {}),
  });
  return handle(res);
};

// v2.3 Ch1 — fit the model's overconfidence factor from settled results and store it
// (the engine applies it on the next build). Returns the fit summary + a message.
export const fitCalibration = async (): Promise<{
  factor: number; n: number; ready: boolean; applied: boolean; message: string;
}> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/calibration/fit`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return handle(res);
};

export const getStrategyRuns = async (limit = 20): Promise<StrategyRun[]> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs?limit=${limit}`, {
    headers: { ...authHeader() },
  });
  return handle(res);
};

export const getStrategyRun = async (id: string): Promise<StrategyRun> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${id}`, {
    headers: { ...authHeader() },
  });
  return handle(res);
};

export const deleteStrategyRun = async (id: string): Promise<void> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${id}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  await handle(res);
};

export const confirmStrategyBet = async (
  runId: string,
  betIndex: number,
  overrides?: { price?: number; stake?: number; takeSP?: boolean }
): Promise<{ success: boolean; status: string; bet: any }> => {
  const res = await strategyFetch(
    `${API_BASE_URL}/api/strategy/runs/${runId}/bets/${betIndex}/confirm`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(overrides || {}) }
  );
  return handle(res);
};

export const reviewStrategyRun = async (runId: string): Promise<StrategyRun> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${runId}/review`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return handle(res);
};

// ── Results Reporting Dashboard (GET /api/strategy/report) ──────────────────
// Pure deterministic report over the user's strategy runs. P&L/ROI/turnover are
// LIVE-only (real money); strike / CLV use LIVE + PAPER together.
export interface ReportMetrics {
  bets: number; bets_live: number;
  turnover: number | null; pnl: number | null; roi: number | null;
  strike: number | null; on_base_rate: number | null; clv: number | null;
  n: number; flag: 'ok' | 'thin' | 'too_few';
}
export interface EngineMetrics extends ReportMetrics { engine: string; }
export interface DayMetrics extends ReportMetrics { day: string; }
export interface StakingSimBook {
  n: number;
  flat: { stake: number; pnl: number | null; roi: number | null };
  quarter_kelly: { stake: number; pnl: number | null; roi: number | null };
  dutch: { stake: number; pnl: number | null; roi: number | null };
}
export interface MultibetReport {
  leg1: ReportMetrics; leg2: ReportMetrics;
  verdict: 'gathering' | 'widen' | 'disable' | string; verdict_note: string; rule: string;
}
export interface ReportRecord {
  date: string; meeting: string; race: number; runner: string;
  engine: string; mode: 'LIVE' | 'PAPER'; side: 'back' | 'lay';
  market_type: 'WIN' | 'PLACE'; structure: 'single' | 'accumulator'; leg: number | null;
  stake: number; price_taken: number | null; bsp: number | null;
  won: boolean; return: number; clv: number | null;
}
export interface StrategyReport {
  config: Record<string, number>;
  aggregate: { both: ReportMetrics; live: ReportMetrics; paper: ReportMetrics };
  equity_curve: Array<{ date: string; live: number; paper: number }>;
  by_day: DayMetrics[];
  by_engine: EngineMetrics[];
  engine_correlation: { correlation: number | null; n_days: number; note?: string };
  staking_sim: { on_base: StakingSimBook; value: StakingSimBook; all: StakingSimBook; note: string };
  multibet: MultibetReport;
  focus: {
    focus: Array<{ engine: string; day: string; n: number; clv: number | null; roi: number | null; strike: number | null; flag: string }>;
    review: Array<{ engine: string; day: string; n: number; clv: number | null; roi: number | null; strike: number | null; flag: string }>;
    insufficient: Array<{ engine: string; day: string; n: number }>;
    rule: string;
  };
  dates: string[];
  total_records: number;
  records: ReportRecord[];
}

export const getStrategyReport = async (params?: {
  from?: string; to?: string; meeting?: string; mode?: 'live' | 'paper';
}): Promise<StrategyReport> => {
  const qs = new URLSearchParams();
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.meeting) qs.set('meeting', params.meeting);
  if (params?.mode) qs.set('mode', params.mode);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/report${suffix}`, {
    headers: { ...authHeader() },
  });
  return handle(res);
};

// ── Rolling engine v2.6b — the autonomous loop's UI toggle ──────────────────
export interface RollingControl {
  enabled: boolean;          // master on/off for the loop
  mode: 'paper' | 'live';    // THE paper vs real-money switch
  armed: boolean;            // start/kill — live placement only when armed
  max_single_bet?: number;   // hard per-bet cap (Auto page)
  min_stake?: number;        // sub-this stake is skipped (Auto page)
  flow_bankroll?: number;    // Flow ring-fence: daily exposure cap (Flow page)
  flow_stake?: number;       // Flow ring-fence: per-bet size (Flow page)
  flow_enabled?: boolean;    // Flow engine independent kill switch (Flow page)
  interval_min?: number;     // loop cadence (min) — runtime-adjustable from the Auto page
  auto_build_enabled?: boolean;      // autonomous re-selection on/off (Auto page's own engine)
  auto_build_interval_min?: number;  // autonomous re-selection cadence (min)
}
export interface AutoBuildStatus {
  enabled: boolean; interval_min: number; ready: boolean; busy: boolean;
  last: { ts: string | null; ok: boolean; bets: number | null; error: string | null; note: string | null };
}
export interface RollingNotPlaced {
  runner?: string; race?: string; action?: string; reason?: string; mins_to_jump?: number | null;
}
export interface RollingCycleSummary {
  at: string; mode: string; armed?: boolean; date?: string;
  scoped?: number; decisions?: number; placed: number; failed?: number;
  open_exposure_after?: number; bank?: number; skips?: string[]; note?: string;
  next_jump_mins?: number | null; not_placed?: RollingNotPlaced[];
}
export interface RollingStatus {
  control: RollingControl;
  config: Record<string, any>;
  params: {
    paper_bank: number; max_single_bet: number; min_stake?: number; window_min: number; interval_min: number;
    place_from_min: number; onbase_flat: number; value_fraction: number; mmove_mode: string;
    allow_in_play?: boolean;
    onbase_window_min?: number; interval_sec?: number; cadence_effective_sec?: number;
    offer_engine_enabled?: boolean; offer_sp_guarantee?: boolean;
  };
  live_balance: number | null;   // real Betfair availableToBet (null offline / paper)
  loop_running: boolean;
  loop_wired?: boolean;
  last_cycle?: RollingCycleSummary | null;
  auto_build?: AutoBuildStatus;  // the Auto page's own autonomous re-selection engine
  concordance_graduation?: ConcordanceGraduation | null;
}

// Concordance paper→live graduation progress (surfaced on the Auto page).
export interface ConcordanceGraduation {
  on_probation: boolean;
  promoted_at: string | null;
  n: number; n_target: number; bets_remaining: number;
  roi_flat_pct: number | null;
  clv_pct: number | null;
  ready: boolean;
  reasons: string[];
  updated_at: string | null;
}
// Force one autonomous re-selection now (ignores the on/off switch — for testing the button).
export const runAutoBuildNow = async (): Promise<AutoBuildStatus['last']> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/auto-build/run`, { method: 'POST', headers: jsonHeaders(), body: '{}' });
  return handle(res);
};

// Run one real loop cycle now (respects window + live/armed gating; paper just simulates).
export const runLoopCycle = async (): Promise<RollingCycleSummary> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/loop/run`, {
    method: 'POST', headers: jsonHeaders(), body: '{}',
  });
  return handle(res);
};

export const getRollingStatus = async (): Promise<RollingStatus> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/status`, { headers: { ...authHeader() } });
  return handle(res);
};

export const setRollingControl = async (
  patch: Partial<RollingControl>
): Promise<{ control: RollingControl }> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/control`, {
    method: 'POST', headers: jsonHeaders(), body: JSON.stringify(patch),
  });
  return handle(res);
};

export interface PaperDecision {
  runner?: string; track?: string; race?: number; engine: string;
  barrier?: number | null; surface?: string | null;
  origin_engine?: string | null; execution_lane?: string | null;
  action: string; stake: number; reason?: string; price?: number | null; mins_to_jump?: number;
}
export interface PaperCycleResult {
  date?: string; paper_bank: number; scoped: number; committed: number; placed: number;
  decisions: PaperDecision[]; note?: string;
  view?: 'window' | 'full'; window_min?: number; card_size?: number; next_jump_mins?: number | null;
}
// Run a paper cycle on demand — sizes the current picks off the paper bank, no money.
// Default mirrors the live loop: only races jumping in the next 30 min. `full` previews
// the whole card (window ignored) for when nothing is imminent.
export const runPaperCycle = async (full = false): Promise<PaperCycleResult> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/cycle${full ? '?full=1' : ''}`, {
    method: 'POST', headers: jsonHeaders(), body: '{}',
  });
  return handle(res);
};

// ── v2.8 §1a — 24-hour pre-race candidate slate ──────────────────────────────
export interface SlateRow {
  race: string; track?: string; runner: string; engine: string; race_time?: string;
  hours_to_jump: number; current_price?: number | null; price_24h_ago?: number | null;
  frozen_price?: number | null; live_price?: number | null; price_gap?: number | null;
  trajectory_points: number; entry_window_target: number; status: string;
  offer_opens_at?: string; stake_est?: number; bucket?: string;
  bucket_clv_pct?: number | null; bucket_n?: number;
  market_status?: string | null; data_excluded?: boolean; exclude_reason?: string;
}
export interface ExcludedMeeting { meeting: string; races: number; with_price: number; coverage: number; reason: string; }
// v2.13 Rev B — tick-loop watch candidate (Concordance / drift re-check each 60s tick).
export interface WatchRow {
  race: string; track?: string; runner: string; engine: string; model_fair?: number | null;
  live_price?: number | null; market_rank?: number | null;
  concordance_band?: string | null; concordance_eligible?: boolean; source?: string; status: string;
}
export interface SlateResult {
  date?: string; window_hours?: number; sizing_bank?: number; slate: SlateRow[]; note?: string;
  watch?: WatchRow[]; excluded?: SlateRow[]; excluded_meetings?: ExcludedMeeting[];
}
export const getRollingSlate = async (): Promise<SlateResult> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/slate`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Bot activity — what's actually been placed/worked today (post-bet) ────────
export interface ActivityBet {
  race: string; runner: string; engine: string; bucket?: string;
  origin_engine?: string | null;   // the engine that GENERATED the candidate
  execution_lane?: string | null;  // the lane that actually EXECUTED it (or the paper gate that stopped it)
  barrier?: number | null; surface?: string | null;
  stake?: number | null; price?: number | null; matched_price?: number | null;
  size_matched?: number | null; status: string; live?: boolean; placed_at?: string | null;
  offer_posted?: number | null; offer_current?: number | null; n_amendments?: number | null;
  clv_pct?: number | null;
  // Persistent disposition ledger — a pick that did NOT become a matched/working bet keeps
  // its row with the reason it did not fire, instead of dropping off once the race jumps.
  disposition_reason?: string | null; resulted?: boolean; pending?: boolean; scratched?: boolean;
  jump_time?: string | null;
}
export interface ReasonRollup { code: string; count: number; label: string; actionable: string; bucket: string; }
export interface ActivityResult {
  date?: string; count?: number; placed?: number; working?: number;
  did_not_fire?: number; pending?: number; loop_last_cycle?: string | null;
  rollup?: ReasonRollup[];
  by_lane?: Record<string, { live: number; paper: number }>; // routing summary: what each lane executed
  by_origin?: Record<string, number>;                        // which engine generated the candidates
  bets: ActivityBet[];
}
export const getRollingActivity = async (date?: string): Promise<ActivityResult> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/activity${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.28 Rev B §5 — the immutable Live Lifecycle Grid ───────────────────────
// display_sequence_no is assigned ONCE by the backend on first admission. The client renders
// ascending by it and applies NO second sort: §5.3 forbids the production page from re-ordering by
// price, stake, status, matched amount, offer_at, jump time or result.
export interface StakeTrace {
  p_cal: number | null; calibrated_fair_price: number | null;
  selection_conviction: number | null; selection_conviction_basis?: string;
  policy_prior: number | null; policy_prior_basis?: string; policy_prior_pool_n?: number;
  live_learning_multiplier: number | null; live_learning_basis?: string; live_n?: number;
  agreement_contribution: number | null; flow_contribution: number | null;
  lane_cap: number | null; race_cap: number | null; bank_cap: number | null; risk_multiplier: number | null;
  effective_fraction: number | null; min_bet: number; max_bet: number;
  uncapped_base_target: number; technical_cap: number | null; final_base_target: number;
  ratchet_max_stake: number | null; ratchet_state: string | null;
  agreement_ratio: number | null; agreement_band: string | null;
  cumulative_target: number; top_up_required: number; final_executable_stake: number;
  caps_applied: string[]; binding_cap: string; reason_code: string | null;
  endpoint_saturation: string | null; stake_reason_text: string;
}
export interface LedgerRow {
  display_sequence_no: number; row_key: string; date: string;
  race_id: string; runner_id: string; runner: string; track: string; race: number;
  // WIN | PLACE — part of row identity; same selection can have both.
  market?: 'WIN' | 'PLACE' | string | null;
  market_id?: string | null;
  barrier: number | null; surface: string | null; jump_at: string | null;
  // §2.1 four identities
  candidate_source_engine: string | null; authorising_live_policy: string | null;
  authorising_live_policy_id: string | null; execution_policy: string | null;
  overlap_tags: string[]; authority_live: boolean; policy_id: string | null;
  // Set when the lane would be live but for an operator probation flag — i.e. paper BY DESIGN,
  // not a routing failure. The grid tones these rows differently on the strength of it.
  paper_probation_lane: string | null;
  // §6.1 four prices
  calibrated_fair_price: number | null; decision_price: number | null;
  current_executable_price: number | null; predicted_bsp: number | null;
  quote_age_s: number | null; available_depth: number | null; price_trend: string | null;
  agreement_ratio: number | null;
  p_cal: number | null; model_rank: number | null; reliability: number | null;
  flow: Record<string, unknown> | null;
  // §7 timing
  offer_at: string | null; time_to_jump: number | null; sp_hard_deadline: string | null;
  timing_policy_id: string | null; monitor_from: string | null;
  // §8.2 stake
  stake_trace: StakeTrace | null; base_target: number | null; cumulative_target: number | null;
  top_up_required: number | null; binding_cap: string | null;
  ratchet: { state: string; band: string | null; max: number | null; cumulative: number } | null;
  // Live Grid Bucket Toggle + Staking Update — set only for BUCKET_KELLY.-authorised rows; a
  // completely separate derivation from stake_trace above, which belongs to the other sizers.
  // Formula updated 2026-08-31: bucket allocation x agreement weighting, no raw Kelly/commission/cap.
  bucket_kelly: {
    policy_id: string | null; rated_price: number | null; market_price: number | null;
    agreement: number | null; agreement_weight: number | null;
    bucket_allocation_scale: number | null; bucket_allocation: number | null;
    total_weight_in_bucket: number | null; share: number | null;
    staking_bank: number | null; stake_pre_increment: number | null;
  } | null;
  // §10 fill
  matched_stake?: number; active_working_stake?: number; sp_protected_stake?: number;
  internal_unreleased_stake?: number; average_matched_price?: number | null;
  execution_state?: string | null;
  // status / outcome
  status: string; terminal_reason_code: string | null; reason_text: string | null;
  // Non-fatal conditions on the row. 'DROPPED_WHILE_WORKING' means the build stopped proposing this
  // runner while it still held exposure on the exchange — the order was not withdrawn by the drop.
  warnings?: string[];
  result?: string | null; bsp?: number | null; clv?: number | null; pnl?: number | null;
  first_listed_at: string | null; last_dynamic_update_at: string | null;
  audit?: { at: string; field: string; from: unknown; to: unknown }[];
}
export interface LedgerResult {
  date: string; rows: LedgerRow[];
  summary: { live: number; paper: number; matched: number; working: number; pending: number;
    did_not_fire: number; settled: number; exposure: number };
  faults: { seq: number; runner: string; type: string; [k: string]: unknown }[];
  timing: { monitoring_horizon_h: number; entry_policy: string; hard_completion_deadline_min: number };
}
export const getStrategyLedger = async (date?: string): Promise<LedgerResult> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/ledger${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── The three v2.21/v2.22/v2.24 portfolio lanes — read-only decision panels ───
export interface OverlayCand {
  race_id: string; runner_name: string | null; source_engines?: string[];
  calibrated_rated_price: number | null; p_cal: number | null; price_lock: number | null;
  overlay_multiple: number | null; edge_net: number | null; min_acceptable_price: number | null;
  selected_rank: number | null; reserve_rank: number | null; reason_code: string; policy_id?: string | null;
}
export interface OverlayToday {
  date: string; enabled: boolean; live: boolean; note: string;
  lock: null | {
    slate_complete: boolean; terminal_reason_code?: string; lock_at?: number | null; locked_at?: string;
    selected: OverlayCand[]; reserves: OverlayCand[]; leaderboard: OverlayCand[];
  };
}
export const getOverlayToday = async (date?: string): Promise<OverlayToday> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/overlay/today${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface ResolutionDecision {
  track: string; race: number; fired: boolean; reason_code: string; candidate: string | null;
  model_rank_candidate: number | null; p1: number | null; p2: number | null; p3: number | null;
  pair_probability: number | null; pair_to_third_ratio: number | null; pair_to_third_abs_gap: number | null;
  preference_ratio: number | null; persist_count: number | null; market_to_fair_ratio: number | null;
  decision_price: number | null; factor_concurrence_count: number | null; max_single_factor_share: number | null;
  data_completeness: number | null;
}
export interface ResolutionToday {
  date: string; enabled: boolean; live: boolean; note: string; fired: number; evaluated: number;
  decisions: ResolutionDecision[]; bets: any[];
}
export const getResolutionToday = async (date?: string): Promise<ResolutionToday> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/resolution/today${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface RobustValueCand {
  track: string; race: number | string; runner: string; price: number | null;
  lane: string; reason_code: string; live: boolean; conviction: number | null;
  intrinsic_strength: number | null; breadth_score: number | null; robustness_score: number | null;
  edge_net: number | null; edge_score: number | null; staking_pool?: string; stake?: number | null;
  loo?: { top2: number | null; survival: number | null; fair_price_cv: number | null; collapse_factor_ids?: number[] };
  perturb?: { top2: number | null; survival: number | null; rank_volatility: number | null };
}
export interface RobustValueToday {
  date: string; enabled: boolean; live: boolean; note: string; evaluated: number;
  by_lane: Record<string, number>; candidates: RobustValueCand[];
}
export const getRobustValueToday = async (date?: string): Promise<RobustValueToday> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/robust-value/today${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.12 §6 — criterion-based Buckets page (read-only) ──────────────────────
export interface BucketRow {
  policy_id: string; label: string; engine: string; state: string; criterion: string; book: string;
  n: number; live_n: number;
  avg_price?: number | null; price_std?: number | null; n_below_8?: number; n_above_8?: number;
  paper_clv_pct?: number | null; realised_clv_pct?: number | null;
  win_roi_pct?: number | null; place_roi_pct?: number | null; place_route?: string;
  n_min?: number; n_min_progress?: string; window_closed?: boolean;
  variance_tier?: string; variance_gap_pct?: number | null; pre_migration?: boolean;
}
export interface BucketsResult { from?: string; to?: string; n_bets?: number; buckets: BucketRow[]; }
export const getStrategyBuckets = async (book: 'all' | 'A' | 'B' = 'all'): Promise<BucketsResult> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/buckets?book=${book}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Live Grid Bucket Toggle + Staking Update — the bucket registry ────
// Formula updated 2026-08-31: allocation_scale (renamed from kelly_scale) is now the bucket's
// fixed daily risk budget as a fraction of the bank, not a Kelly multiplier; no per-bet cap or
// commission term any more.
export interface BucketRegistryRow {
  policy_id: string; label: string | null; live_enabled: boolean; allocation_scale: number;
  control_kind?: 'bucket' | 'brain_push' | 'manual'; can_edit?: boolean; control_note?: string;
  stream_id?: string; revision?: string | null;
}
export interface BucketRegistryResult {
  can_edit?: boolean;
  config_version: string;
  buckets: BucketRegistryRow[];
  default_for_unlisted: { live_enabled: boolean; allocation_scale: number };
  global: {
    agreement_exponent: number; profit_compound_fraction: number;
    contributed_capital: number | null; profit_high_water: number | null; settled_equity: number | null;
  };
}
// Owner 2026-10-08: Bet buckets Rules tab.
export interface BucketRuleGate { name: string; rule: string }
export interface BucketRule { policy_id: string; label: string; live: boolean; market: string; bets_21d: number; picks: string; recorded_rule: string | null;
  timing: string; gates: BucketRuleGate[]; staking: string; measured: { roi_label: string; status: string; note: string } }
export interface BucketRulesResponse { version: string; generated_at: string; universal: BucketRuleGate[];
  auto: { mode: string | null; armed: boolean; enabled: boolean; win_stake: number | null; place_stake: number | null; brain_stake: number | null; longshot_stake: number | null };
  buckets: BucketRule[] }
export const getBucketRules = async (): Promise<BucketRulesResponse> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bucket-rules`, { headers: { ...authHeader() } });
  return handle(res);
};
export const getBucketRegistry = async (): Promise<BucketRegistryResult> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bucket-registry`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Bucket bets drill-down (per-bucket past-bets page) ───────────────────────
export interface BucketDrawdownMetrics {
  max_drawdown_days?: number | null; current_drawdown_days?: number | null;
  max_drawdown_days_bets?: number | null; max_drawdown_bets_days?: number | null;
  current_drawdown_start?: string | null; drawdown_as_of?: string | null; drawdown_days_basis?: string;
  max_drawdown_bets?: number | null; current_drawdown_bets?: number | null;
  avg_bsp?: number | null; avg_winner_bsp?: number | null; winners_priced_n?: number;
  bets_per_day?: number | null; bets_per_day_days?: number | null;
  days_between_wins?: number | null; longest_days_without_win?: number | null; days_since_last_win?: number | null; win_days_n?: number;
}
export interface BucketOption extends BucketDrawdownMetrics {
  max_drawdown_bets?: number | null; current_drawdown_bets?: number | null;
  history_coverage?: { status: string; note: string } | null;
  policy_id: string; label: string; n: number; settled_n: number;
  backtest_n?: number; backtest_priced_n?: number; forward_n?: number; roi_label?: string;
  roi?: number | null;                // flat-stake ROI %, same formula as the detail view; null if nothing settled/priced yet
  verified_n?: number;                // priced bets actually placed or recorded before the race (no backtest/replay rows)
  verified_roi?: number | null;       // flat-stake BSP ROI % on those verified forward bets
  last_bet_date?: string | null;      // most recent bet date in this bucket — shows staleness at a glance
  retirement_note?: string | null;    // set when a quiet bucket is retired ON PURPOSE (see server-side RETIREMENT_NOTES)
}
export interface BucketBet {
  start_time?: string | null; bucket_label?: string; policy_id?: string;
  retrospective?: boolean; research_backtest_kind?: string | null;
  date: string | null; track: string | null; race: number | null; runner: string | null;
  engine: string | null; side: string; market_type: string;
  result: 'win' | 'lose' | 'void' | 'pending'; placed: boolean; mode: 'LIVE' | 'PAPER';
  bsp: number | null; rated_price: number | null; clv: number | null;
  stake: number | null; price_taken: number | null; pnl: number | null; void_reason: string | null;
  graded_live?: boolean;   // result/BSP recovered on the fly from the settled market
}
export interface BucketBetsList { from?: string; to?: string; n_bets: number; buckets: BucketOption[]; }
export interface BucketBetsDetail extends BucketDrawdownMetrics {
  forward_drawdown?: BucketDrawdownMetrics | null;
  max_drawdown_bets?: number | null; current_drawdown_bets?: number | null; drawdown_bets_basis?: string;
  avg_bsp?: number | null; strike_rate_pct?: number | null; max_drawdown_units?: number | null; metrics_priced_n?: number; drawdown_basis?: string;
  bucket: string; label: string; n: number; settled_n: number; pending_n: number; graded_live?: number; roi: number | null; forward_roi?: number | null; bets: BucketBet[];
  last_bet_date?: string | null; retirement_note?: string | null;
}
// No bucket → the dropdown list (present buckets). With bucket → that bucket's bets.
export const getBucketList = async (from?: string, to?: string): Promise<BucketBetsList> => {
  const q = new URLSearchParams(); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bucket-bets?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};
export const getBucketBets = async (policyId: string, from?: string, to?: string): Promise<BucketBetsDetail> => {
  const q = new URLSearchParams({ bucket: policyId }); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bucket-bets?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Surface-Band Buckets (Paul, 2026-09-01) — a second, orthogonal classification dimension ──
// keyed on surface (turf/synthetic) + calibrated win-probability band, not engine/state/criterion.
// Every scored runner in every race is tagged (not just staged bets); none of the 5 buckets ever
// authorise real money in this build. Turf Sweet Spot carries a `promotion` verdict (statsGate
// over its Rule-B picks — one bet per race, the highest-probability qualifier).
export interface SurfaceBandPromotion { verdict: 'PROMOTE' | 'CULL' | 'HOLD'; n: number; mean: number | null; lcb: number | null; ucb: number | null; reason: string; }
export interface SurfaceBandRow {
  key: string; label: string | null; status: 'paper' | 'probation' | 'exclusionary' | 'unlisted'; live_enabled: boolean;
  n: number; wins: number; strike: number | null; roi: number | null; pnl: number;
  promotion: SurfaceBandPromotion | null;
}
export interface SurfaceBandRegistryResult { enabled: boolean; from?: string; to?: string; buckets: SurfaceBandRow[]; }
export const getSurfaceBandRegistry = async (from?: string, to?: string): Promise<SurfaceBandRegistryResult> => {
  const q = new URLSearchParams(); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/surface-bands?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface SurfaceBandRunner {
  date: string | null; track: string | null; race: number | null; runner: string | null; surface: string | null;
  model_rank: number | null; market_rank: number | null; model_prob: number | null; model_fair: number | null;
  turf_sweet_spot_pick: boolean; result: 'win' | 'lose' | 'pending' | string; bsp: number | null; pnl: number | null;
}
export interface SurfaceBandRunnersResult { bucket: string; label: string | null; n: number; runners: SurfaceBandRunner[]; }
export const getSurfaceBandRunners = async (bucket: string, from?: string, to?: string): Promise<SurfaceBandRunnersResult> => {
  const q = new URLSearchParams({ bucket }); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/surface-band-runners?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Orthogonal Factor-Family Buckets (Paul, 2026-09-01) — a THIRD, independent classification ──
// dimension: 9 research families (recent form, beaten margin, rank delta, market/model ratio,
// previous BF price, recency, barrier, race shape, age, weight-vs-field), ~60 buckets total. A
// runner can land in buckets from several families at once (families, not gates); none authorise
// cash. Each bucket carries 3 reporting windows (last 50 / last 100 / entire forward sample).
export interface FactorFamilyStatBlock {
  n: number; wins: number; strike: number | null; expected_strike: number | null; actual_over_expected: number | null;
  gross_roi: number | null; net_roi: number | null; trimmed_roi: number | null;
  avg_bsp: number | null; median_bsp: number | null; cumulative_pnl: number;
  max_drawdown: number; bootstrap_ci_90: { lo: number | null; hi: number | null; mean: number | null };
}
export interface FactorFamilyBucketRow { key: string; last_50: FactorFamilyStatBlock; last_100: FactorFamilyStatBlock; entire_sample: FactorFamilyStatBlock; }
export interface FactorFamilyGroup { family: string; buckets: FactorFamilyBucketRow[]; }
export interface FactorFamiliesResult { enabled: boolean; from?: string; to?: string; families: FactorFamilyGroup[]; }
export const getFactorFamilies = async (from?: string, to?: string): Promise<FactorFamiliesResult> => {
  const q = new URLSearchParams(); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/factor-families?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface FactorFamilyRunner {
  date: string | null; track: string | null; race: number | null; runner: string | null;
  context: Record<string, number | string | null> | null;
  result: 'win' | 'lose' | 'pending' | string; bsp: number | null; pnl: number | null;
}
export interface FactorFamilyRunnersResult { bucket: string; n: number; runners: FactorFamilyRunner[]; }
export const getFactorFamilyRunners = async (bucket: string, from?: string, to?: string): Promise<FactorFamilyRunnersResult> => {
  const q = new URLSearchParams({ bucket }); if (from) q.set('from', from); if (to) q.set('to', to);
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/factor-family-runners?${q.toString()}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.7 Autonomous Graduation & Culling — the bucket board ──────────────────
export interface GraduationFlags {
  place_live: boolean; lay_live: boolean; band_over8: boolean; state4_live: boolean; allow_50plus_live: boolean;
}
export interface BucketVerdict {
  policy_id: string; engine: string; market: string; state_set: string;
  criterion?: string | null;                 // v2.16 §0 — the bucket partition (replaces price band)
  price_band?: string | null;                // null under the criterion key; price is now a covariate
  avg_price?: number | null; price_std?: number | null; n_below_8?: number | null; n_above_8?: number | null;
  book: 'A' | 'B'; current_state: 'live' | 'paper'; next_state: 'live' | 'paper';
  action: 'promote' | 'cull' | 'hold'; reason: string; judged_on: string;
  is_new_behaviour: boolean; new_behaviour_kind: string | null; bootstrap: boolean; live_allowed: boolean;
  n: number; live_n: number;
  paper_clv: number | null; realised_clv: number | null; slippage_clv: number | null;
  mean_clv: number | null; lcb: number | null; max_drawdown: number; longest_losing_run: number;
  turnover: number; gross_pnl: number;
}
export interface GraduationBoard {
  buckets: BucketVerdict[];
  cost: {
    monthly_turnover: number; gross_pnl: number; net_after_data: number; net_after_data_dev: number;
    roi_before_cost: number | null; roi_after_cost: number | null; monthly_fixed_cost: number;
    turnover_to_cover: { '2pct': number; '3pct': number; '5pct': number };
  };
  summary: { total_buckets: number; a_book: number; b_book: number; promotions: number; culls: number };
  control: { frozen: boolean; feature_flags: GraduationFlags };
  params: { n_min: number; slippage_buffer: number; active_policies: number; monthly_fixed_cost: number };
}

export const getGraduationBuckets = async (): Promise<GraduationBoard> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/graduation/buckets`, { headers: { ...authHeader() } });
  return handle(res);
};
export const setGraduationControl = async (
  patch: { frozen?: boolean; feature_flags?: Partial<GraduationFlags> }
): Promise<{ control: { frozen: boolean; feature_flags: GraduationFlags } }> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/graduation/control`, {
    method: 'POST', headers: jsonHeaders(), body: JSON.stringify(patch),
  });
  return handle(res);
};

// ── v2.16 Part B — Turnover-Cover staking solve ──────────────────────────────
export interface StakeSolveBucket {
  policy_id: string; n: number; wins: number; freq: number; avg_price: number | null;
  realised_strike: number | null; implied_strike: number | null; s: number | null;
  prior: number; w: number; cf: number; mu: number | null; clv_graduated: boolean | null;
  stake: number; is_workhorse: boolean;
}
export interface StakeSolve {
  from: string | null; to: string | null; n_bets: number; staking_engine: string;
  min_bet: number; max_bet: number; margin: number; floor_bet: number;
  solve: { basis: string; delta: number | null; floor_cost: number; workhorse_lever: number; n_workhorse: number; n_floor: number };
  buckets: StakeSolveBucket[];
}
export const getStakeSolve = async (minBet?: number): Promise<StakeSolve> => {
  const q = minBet != null ? `?min_bet=${minBet}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/stake-solve${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.16 Part C — Bankroll Safeguard ────────────────────────────────────────
export interface BankSafeguard {
  min_bet: number; solved_max_bet: number; dd_multiplier: number; n_settled: number; undersampled_flag: boolean;
  dd_1d: number; dd_7d: number; dd_30d: number;
  required_bank_1d: number; required_bank_7d: number; required_bank_30d: number;
  required_raw_1d: number; required_raw_7d: number; required_raw_30d: number; ratcheted: boolean;
}
export const getBankSafeguard = async (minBet?: number): Promise<BankSafeguard> => {
  const q = minBet != null ? `?min_bet=${minBet}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bank-safeguard${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.16 Part A — Flow Engine status ────────────────────────────────────────
export interface FlowStatus {
  mode: string; places_bets: boolean; note: string;
  config: Record<string, number>; defaults: Record<string, number>;
  stores: { flow_reads: number; flow_picks: number };
}
export const getFlowStatus = async (): Promise<FlowStatus> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/flow/status`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── v2.16 Part A — Flow Engine paper-trade ledger (shadow picks + reasoning) ──
export interface FlowPaperPick {
  pick_id: string; race_id: string; runner_id: string | number; runner_name: string | null;
  regime: string; entered_ts: string; signal_at_entry: number | null;
  offer_price: number | null; matched_price: number | null;
  stake: number; size_matched: number | null; size_stage: string;
  block_reason: string | null; mode: string;
  surface: string | null; context_key: string | null;
  window_state: string | null; gates_passed: boolean | null;
  spike_z: number | null; under_reaction: number | null; q_move: number | null;
  dumb_money_flag: boolean | null; qualified: boolean; reason: string;
}
export interface FlowPaper {
  mode: string; places_bets: boolean; note: string;
  n_picks: number; total_reads: number;
  by_regime: { A: number; B: number }; dumb_money_fades: number;
  ring_fence: { bankroll: number; stake: number; used_today: number; bets_today: number };
  picks: FlowPaperPick[];
}
export const getFlowPaper = async (limit = 200, date?: string): Promise<FlowPaper> => {
  const q = `?limit=${limit}${date ? `&date=${date}` : ''}`;
  const res = await strategyFetch(`${API_BASE_URL}/api/flow/paper${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface FlowVerdict { looking_for: string; regime: 'A' | 'B' | null; signal: number; would_fire: boolean; blocker: string | null; window_state: string | null; runner_id?: number | null; runner_name?: string | null; }
export interface FlowWatch { race_id: string; mins_to_jump: number; window_open: boolean; snapshots: number; reads: number; total_matched: number | null; verdict?: FlowVerdict | null; }
export interface FlowCapture { ts: string | null; due: number; books: number; snaps_written: number; error: string | null; note: string | null; }
export interface FlowRead {
  race_id: string; runner_id: string | number; pass_ts: string;
  regimeA: boolean; regimeB: boolean; spike_z: number | null; q_move: number | null;
  under_reaction: number | null; dumb: boolean; window_state: string | null; gates_passed: boolean | null;
}
export interface FlowActivity {
  capture_enabled: boolean; flow_mode: string; flow_enabled?: boolean; now: string;
  window_hours: number; window_open_min: number; markets_in_window: number;
  last_read_ts: string | null; note: string; last_capture?: FlowCapture;
  watching: FlowWatch[]; recent_reads: FlowRead[];
}
export const getFlowActivity = async (): Promise<FlowActivity> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/flow/activity`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── Section conversations (day / race / bet) ────────────────────────────────
export type ChatScope = 'day' | 'race' | 'bet';

export interface ChatRef {
  track?: string;
  race?: number;
  betIndex?: number;
}

export interface ThreadMessage {
  role: 'user' | 'assistant';
  text: string;
  at: string;
}

export interface Thread {
  id: string;
  scope: ChatScope;
  ref: ChatRef | null;
  ref_key: string;
  label: string | null;
  messages: ThreadMessage[];
  updated_at: string;
}

export const getThreads = async (runId: string): Promise<Thread[]> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${runId}/threads`, {
    headers: { ...authHeader() },
  });
  return handle(res);
};

export const sendChat = async (
  runId: string,
  scope: ChatScope,
  ref: ChatRef,
  message: string
): Promise<Thread> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/runs/${runId}/chat`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ scope, ref, message }),
  });
  return handle(res);
};

// ── Settings page (admin) — client-tunable engine knobs (UI → .env → default) ──
export interface SettingField {
  key: string;
  label: string;
  type: 'bool' | 'number' | 'string' | 'enum';
  help: string | null;
  default: number | string | boolean | null;
  min: number | null;
  max: number | null;
  step: number | null;
  options: string[] | null;
  env_value: string | null;
  override: string | null;
  effective: string | null;
  source: 'ui' | 'env' | 'default';
}
export interface SettingGroup {
  id: string;
  title: string;
  blurb: string;
  live: boolean;
  fields: SettingField[];
}
export interface SettingsResponse { groups: SettingGroup[]; }
export interface SaveSettingsResult {
  applied: { key: string; value: string }[];
  cleared: string[];
  rejected: { key: string; reason: string }[];
  overrides: Record<string, string>;
}

export const getSettings = async (): Promise<SettingsResponse> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/settings`, { headers: { ...authHeader() } });
  return handle(res);
};

// overrides: key → new value; empty string clears the override (falls back to env).
export const saveSettings = async (
  overrides: Record<string, string>
): Promise<SaveSettingsResult> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/settings`, {
    method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ overrides }),
  });
  return handle(res);
};

// ── Owner items 2/4/5/6 (2026-07-18) — evidence surfaces on the Autonomous Engine page ──────────
export interface PortfolioRow {
  label: string; thesis: string; lanes: string[];
  n: number; winners: number; turnover: number;
  strike: number | null; strike_ci: [number | null, number | null];
  roi: number | null; pnl: number; clv: number | null; avg_price: number | null;
  drawdown: number; largest_winner_share: number | null; ex_top1_roi: number | null;
  pnl_contribution: number | null;
  promotion_state?: { lane: string; state: string }[];
  non_additive?: boolean; note?: string; fault?: boolean;
}
export interface PortfolioBoard {
  as_of: string; settled_bets_considered: number;
  total: { pnl: number; n: number };
  portfolios: Record<string, PortfolioRow>;
  note: string;
}
export const getPortfolios = async (): Promise<PortfolioBoard> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/portfolios`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface ResolutionFunnel {
  date: string; races: number;
  funnel: null | {
    races_evaluated_by_on_base: number; on_base_bets_fired: number;
    exactly_one_permitted_separation_failure: number; multiple_or_non_permitted_failures: number;
    pair_dominance_pass: number; pair_dominance_fail: number;
    market_resolution_pass: number; market_resolution_fail: number;
    factor_completeness_pass: number; factor_completeness_fail: number;
    resolution_live_bets: number; rank1_selected: number; rank2_selected: number;
    terminal_reasons_by_race: Record<string, string>;
    reason_counts: Record<string, number>;
    diagnostics: { code: string; detail: string }[];
  };
  determinism?: { verified_by: string; policy_id: string | null };
  note?: string | null;
}
export const getResolutionFunnel = async (date?: string): Promise<ResolutionFunnel> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/resolution-funnel${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface FlowCard {
  signals: { unique_parents: number; qualifying_events: number; duplicate_events: number; executable_samples: number; unsettled: number };
  performance: { winners: number; strike_rate: number | null; avg_price: number | null; net_pnl: number; net_roi: number | null; clv_mean: number | null };
  robustness: { ex_largest_winner_pnl: number; ex_largest_winner_roi: number | null; ex_top3_pnl: number; profit_concentration: number | null };
  selectivity: { multi_signal_rate: number | null; signals_per_race: number | null };
  data_quality: { z_states: Record<string, number>; missing_price: number; saturation: { saturated: boolean; alert: { code: string; detail: string } | null } };
}
export interface FlowScorecards {
  as_of: string; excluded_meetings: string[]; excluded_count: number;
  by_regime: Record<string, FlowCard>; by_z_band: Record<string, FlowCard>;
  by_multiplicity: Record<string, FlowCard>; by_overlap: Record<string, FlowCard>;
  by_z_quality: Record<string, FlowCard>;
  all: FlowCard; research_policies: string[];
  promotion: { eligible_for_review: boolean; state: string; blocking_reasons: string[]; note: string };
}
export const getFlowScorecards = async (date?: string): Promise<FlowScorecards> => {
  const q = date ? `?date=${date}` : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/flow-scorecards${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface PolicyVersions {
  as_of: string; law: string;
  policies: {
    lane: string; current_policy_id: string; config_hash: string; effective_at: string;
    config: Record<string, unknown>;
    history: { policy_id: string; effective_at: string; config_hash: string;
      previous_policy_id: string | null; changed_keys: { key: string; from: unknown; to: unknown }[];
      state: string | null; retired_at: string | null; promoted_at: string | null }[];
  }[];
}
export const getPolicyVersions = async (): Promise<PolicyVersions> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/policy-versions`, { headers: { ...authHeader() } });
  return handle(res);
};

// ── THE RACES WE DIDN'T BET (Strategy page detail + CSV) ────────────────────
// The page shows the BETS — a filtered view of the model's opinion. "3 bets from 26 races" only
// means something if you can see what the engine thought of the other 23 and why it stood down.
export interface DetailRunner {
  date: string; runner: string; number: number | null; barrier: number | null;
  jockey: string | null; trainer: string | null; weight: string | null; form: string | null;
  model_rank: number; market_rank: number | null;
  win_pct: number | null;          // CALIBRATED — what the engine decided on
  raw_win_pct: number | null;      // the model before correction
  rated_price: number | null;      // calibrated fair
  raw_model_price: number | null;
  market_price: number | null;
  overlay: number | null;          // market ÷ CALIBRATED rated − 1
  gap_from_rank1_pct: number | null; gap_from_rank1_score: number | null;
  reliability: number | null; race_state: number | null; engine_source: string | null;
  vetoed: boolean; veto_rule: string | null; bet: boolean; result: string | null;
  // Paul (2026-09-10): "result data" — the real settled BSP, not just win/lose.
  starting_price: number | null;
  // Paul: "margins... full form for core ranking analysis" — the structured prior-run history
  // behind the compact `form` string above. Each entry mirrors strategy_runner_log's own shape
  // (date, track, distance, track_condition, class, finish, margin, sp, opening_sp, betfair_price,
  // weight, barrier). null when the runner has no recorded prior form.
  recent_runs: Array<Record<string, unknown>> | null;
  total_model_score: number | null; distance: number | null; going: string | null; class_move: number | null;
  best_back: number | null; best_back_size: number | null; best_lay: number | null; best_lay_size: number | null;
  last_traded: number | null; traded_volume: number | null;
  price_quality_state: string | null; price_faults: string[] | null;
  agreement_ratio: number | null;
  final_state: string | null; final_reason: string | null; reason_code: string | null;
  // Paul: "price in snapshots" — the runner's own early → late price movement before the jump.
  earliest_snapshot_price: number | null; earliest_snapshot_minutes_to_jump: number | null;
  latest_snapshot_price: number | null; latest_snapshot_minutes_to_jump: number | null;
  price_drift_pct: number | null; snapshot_count: number;
}
export interface DetailRace {
  race_id: string; date: string; track: string; race: number; field_size: number; runners_scored: number;
  in_slate: boolean; bets: string[]; no_bet_reason: string | null;
  best_overlay: number | null; runners: DetailRunner[];
}
export interface RaceDetailView {
  date: string | null; from: string | null; to: string | null;
  races: DetailRace[]; races_scored: number; races_in_slate: number;
  field_groups: string[];
  // 2026-09-11 incident: a wide date range with Price snapshots on hit 2,409 races in one request
  // and degraded the database badly enough to time out an unrelated report too. Present (non-null)
  // when this response skipped that join for being too wide — never silent, always explained.
  price_snapshots_skipped: { reason: string } | null;
  top_clamped: { reason: string } | null;
  races_capped: { reason: string } | null;
  calibration: { ready: boolean; n: number; fitted_at: string | null; note: string };
  note: string;
}
// "add toggles for what data to pull in the report" (Paul, 2026-09-10) — one group name per toggle;
// matches raceDetail.js's FIELD_GROUPS on the server exactly. Omitted/empty = every group.
export type RaceDetailFieldGroup = 'core' | 'profile' | 'form' | 'market' | 'price_snapshots' | 'result';

export interface RaceDetailQuery {
  date?: string;        // single-day view
  from?: string; to?: string;  // date-range view — either replaces `date`
  top?: number | 'all';
  slate?: 'on' | 'off';
  fields?: RaceDetailFieldGroup[];  // omitted/empty = every group
}

const raceDetailParams = (q: RaceDetailQuery): URLSearchParams => {
  const p = new URLSearchParams();
  if (q.from || q.to) { if (q.from) p.set('from', q.from); if (q.to) p.set('to', q.to); }
  else p.set('date', q.date || new Date().toISOString().slice(0, 10));
  if (q.top != null) p.set('top', String(q.top));
  if (q.slate) p.set('slate', q.slate);
  if (q.fields && q.fields.length) p.set('fields', q.fields.join(','));
  return p;
};

export const getRaceDetail = async (query: RaceDetailQuery): Promise<RaceDetailView> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/race-detail?${raceDetailParams(query)}`, { headers: { ...authHeader() } });
  return handle(res);
};

// The CSV is always the FULL field regardless of the page's top-N — an export exists to be
// re-analysed, and a truncated one silently drops the evidence for the next question.
export const downloadRaceDetailCsv = async (query: RaceDetailQuery): Promise<void> => {
  const q = raceDetailParams(query);
  q.set('format', 'csv');
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/race-detail?${q}`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`CSV export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const rangeLabel = query.from || query.to ? `${query.from || 'start'}_to_${query.to || 'now'}` : (query.date || 'today');
  a.download = `betbuilder-${rangeLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ── MODEL INSPECTION + DECISION TRACE (Corrective Build §2 + §6) ────────────
// Every runner in every race, with its complete decision trace — the exact production outputs,
// assembled, not recomputed. Read-only.
export interface TraceFactor {
  id: string; name: string; weight: number | null;
  raw_score: number | null; weighted_contribution: number | null;
  coverage: number | null; defaulted: boolean;
}
export interface TraceGate { gate: string; detail: string | null }
export interface RunnerTrace {
  runner: string; track: string; race: number; model_rank: number | null;
  total_model_score: number | null; win_pct: number | null; fair_price: number | null;
  coverage_overall: number | null; reliability: number | null;
  factors: TraceFactor[];
  peaking_score: number | null; peaking_coverage: number | null; ability_score: number | null;
  engine_nominated: string | null; race_state: number | null;
  gates_passed: TraceGate[]; gates_failed: TraceGate[];
  primary_rejection_reason: string | null; reason_code: string | null;
  final_authority: { live: boolean; policy: string | null; policy_id: string | null; candidate_source: string | null; reason: string | null };
  final_state: string;
}
export interface InspectionRace {
  race_id: string; track: string; race: number; field_size: number; runners: RunnerTrace[]; all_traced: boolean;
}
export interface InspectionView {
  date: string; races: InspectionRace[]; runners_traced: number;
  factor_meta: { names: Record<string, string>; weights: Record<string, number> }; note: string;
}
export const getInspection = async (date: string): Promise<InspectionView> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/inspection?date=${date}`, { headers: { ...authHeader() } });
  return handle(res);
};
export const downloadInspectionCsv = async (date: string): Promise<void> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/inspection?date=${date}&format=csv`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`CSV export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `betbuilder-inspection-${date}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export const setBucketMode = async (policy_id: string, live_enabled: boolean, allocation_scale: number): Promise<BucketRegistryRow> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/bucket-mode`, {
    method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ policy_id, live_enabled, allocation_scale }),
  });
  return handle(res);
};

// Use each lane's real control endpoint; a Brain mode is not a Core bucket override.
export const setBucketLiveControl = async (row: BucketRegistryRow, enabled: boolean): Promise<BucketRegistryRow> => {
  if (row.can_edit === false || row.control_kind === 'manual') throw new Error('This bucket is read-only');
  if (row.control_kind !== 'brain_push') return setBucketMode(row.policy_id, enabled, 1);
  if (!row.stream_id || row.revision === undefined) throw new Error('Reload Brain controls before saving');
  const res = await strategyFetch(`${API_BASE_URL}/api/rolling/simple-bsp/brain-push`, {
    method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ stream_id: row.stream_id, enabled, expected_revision: row.revision }),
  });
  const data = await handle(res);
  const saved = data.streams?.find((s: { stream_id: string }) => s.stream_id === row.stream_id);
  if (!saved) throw new Error('Reload controls to confirm the Brain setting');
  return { ...row, live_enabled: saved.enabled, revision: saved.revision };
};

export interface BrainMeritPaperBet {
  date: string; race_id: string; runner: string; brain_prob: number; core_prob: number;
  quote: number | null; bsp: number | null; result: string; benchmark_pnl_units: number | null;
  z_top: number; gap_z: number; sample_role: string;
}
export interface BrainMeritPaperSummary {
  policy_id: string; n: number; settled_n: number; unresolved_n: number;
  wins: number; pnl_units: number; roi_pct: number | null;
}
export interface BrainMeritPaperResponse {
  version: string; source_start: string; status: string; retrospective: boolean;
  buckets: BrainMeritPaperSummary[]; bets: BrainMeritPaperBet[];
}
export const getBrainMeritPaperBuckets = async (bucket?: string): Promise<BrainMeritPaperResponse> => {
  const q = bucket ? '?bucket=' + encodeURIComponent(bucket) : '';
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/brain-merit-buckets${q}`, { headers: { ...authHeader() } });
  return handle(res);
};

export interface CoreBrainScenario { n: number; turnover: number; pnl: number; roi_pct: number | null; }
export interface CoreBrainAgreementResponse {
  paper_only: boolean;
  health: { at: string; checked: number; frozen: number; unknown: number; skipped: Record<string, number> } | null;
  replay_period: { from: string; to: string };
  historical: { picks: number; settled: number; counts: Record<string, number>; scenarios: Record<string, CoreBrainScenario> };
  prospective: { picks: number; settled: number; counts: Record<string, number>; scenarios: Record<string, CoreBrainScenario> };
  bets: Array<{ date: string; race_id: string; runner: string; core_policy_id: string; brain_runner: string | null; brain_policy_id: string | null; agreement: string; sample_role: string; result: string; pnl_units: number | null; core_price: number | null; brain_prob: number | null; decided_at: string | null }>;
}
export const getCoreBrainAgreement = async (): Promise<CoreBrainAgreementResponse> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/core-brain-agreement?detail=1`, { headers: { ...authHeader() } });
  return handle(res);
};

// Owner 2026-10-13: Core ranks 1-3 per race for Bucket Next Up.
export interface CoreTop3Race { track: string; race: number; picks: { rank: number; runner: string; number: number | null; result: string | null }[]; }
export const getCoreTop3 = async (date: string): Promise<{ date: string; races: CoreTop3Race[] }> => {
  const res = await strategyFetch(`${API_BASE_URL}/api/strategy/core-top3?date=${encodeURIComponent(date)}`, { headers: { ...authHeader() } });
  return handle(res);
};
